import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import EmptyState from "components/EmptyState";
import { Plus, Search } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { StatusBadge } from "components/Account/StatusBadge";
import Skeleton from "components/Skeleton";

function ProcessorList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [processors, setProcessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);

  const fetchProcessors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (includeInactive) params.set("includeInactive", "true");
      if (search.trim()) params.set("search", search.trim());
      const url = `${apiRoutes.getProcessors}${params.toString() ? `?${params}` : ""}`;

      const res = await makeApiRequest(
        "get",
        url,
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setProcessors(res.data?.data || []);
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to load processors",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcessors();
  }, [includeInactive]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProcessors();
  };

  const formatVolume = (min, max) => {
    if (min == null && max == null) return "Any";
    const minStr = min != null ? `$${min.toLocaleString()}` : "$0";
    const maxStr = max != null ? `$${max.toLocaleString()}` : "Unlimited";
    return `${minStr} - ${maxStr}`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton.Rect className="h-8 w-48 mb-4" />
        <Skeleton.Rect className="h-5 w-full mb-2" />
        <Skeleton.Rect className="h-5 w-full mb-2" />
        <Skeleton.Rect className="h-5 w-2/3" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Processors"
        subtitle="Registry of payment processors and their capabilities."
        actions={
          <button
            className="btn btn-primary"
            onClick={() => navigate("/settings/processors/new")}
          >
            <Plus className="h-4 w-4" />
            Add Processor
          </button>
        }
      />

      <div className="container py-6 space-y-4">
        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                className="input input-bordered w-full pl-10"
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-ghost">
              Search
            </button>
          </form>
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show inactive
          </label>
        </div>

        {/* Table */}
        <section className="surface rounded-xl ring-1 ring-border overflow-hidden">
          {processors.length === 0 ? (
            <EmptyState icon={Search} heading="No processors found" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    {["Name", "Status", "Industries", "Risk", "Monthly Volume", "Boarding"].map(
                      (col) => (
                        <th
                          key={col}
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                        >
                          {col}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {processors.map((p) => (
                    <tr
                      key={p.processorId}
                      className="hover:bg-muted/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/processors/${p.processorId}`)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-foreground">
                        {p.name}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {(p.supportedIndustries || []).slice(0, 3).join(", ")}
                        {(p.supportedIndustries || []).length > 3 && "..."}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            p.riskTolerance === "high"
                              ? "bg-destructive/10 text-destructive"
                              : p.riskTolerance === "medium"
                              ? "bg-warning/10 text-warning"
                              : "bg-success/10 text-success"
                          }`}
                        >
                          {p.riskTolerance}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatVolume(p.minMonthlyVolume, p.maxMonthlyVolume)}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {p.boardingMethod}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default requireAuth(ProcessorList);
